import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AdminApi,
  AdminInvestorProductDocumentsCreateRequest,
  AdminInvestorProductDocumentsDestroyRequest,
  AdminProductListing,
  AdminProductsListPermissionEnum,
  InvestorProductDocument,
  InvestorProductDocumentsApi,
  PureDocumentType,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import { FileWithDocType } from 'src/ui/atoms/file-upload';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { useMultipleBatchFileUploads } from 'src/ui/atoms/file-upload/hooks/use-multiple-batch-file-uploads';
import { AddDocumentsFields, AddDocumentsModalProps, ModalState } from '.';
import { StateValues } from 'react-use-form-state';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import { debounce } from 'lodash';

interface FulfilledResult {
  status: 'fulfilled';
  fileName: string;
  value: InvestorProductDocument;
}

interface RejectedResult {
  status: 'rejected';
  fileName: string;
  reason: any;
}

type PromiseResult = FulfilledResult | RejectedResult;

export const useAddDocuments = ({ onAddNewDocument, onClose }: AddDocumentsModalProps) => {
  const [products, setProducts] = useState<AdminProductListing[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<AdminProductListing | undefined>(undefined);
  const [modalState, setModalState] = useState<ModalState>(ModalState.DEFAULT);
  const [successfulUploadedDocumentIds, setSuccessfulUploadedDocumentIds] = useState<string[]>([]);
  const [noOfDocumentsToUpload, setNoOfDocumentsToUpload] = useState(0);
  const [fileErrors, setFileErrors] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);

  const { makeAuthenticatedApi, withApi, error: apiError } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const investorProductDocumentsApi: InvestorProductDocumentsApi = useMemo(
    () => makeAuthenticatedApi(InvestorProductDocumentsApi),
    [makeAuthenticatedApi],
  );

  const translate = useTranslateWithStringArgs();

  const { documentsUploaded, onChangeBatchDocuments } = useMultipleBatchFileUploads();

  const processBatchUploads = async (
    documents: FileWithDocType[],
    productId: string,
  ): Promise<{
    results: Array<PromiseResult>;
    oversizedFiles: FileWithDocType[];
  }> => {
    const validFiles: FileWithDocType[] = [];
    const oversizedFiles: FileWithDocType[] = [];
    const MAX_FILE_SIZE_BYTES = 5000000;

    // documents that exceed the maximum allowed size are not sent for upload
    documents.forEach((doc) => {
      if (doc.file && doc.file.size > MAX_FILE_SIZE_BYTES) {
        oversizedFiles.push(doc);
      } else {
        validFiles.push(doc);
      }
    });

    if (validFiles.length === 0) {
      return { results: [], oversizedFiles };
    }

    // make an upload request for each document
    const uploadPromises = validFiles.map((document) => {
      const file = document.file as File;
      const params: AdminInvestorProductDocumentsCreateRequest = {
        productId: productId,
        file,
      };
      if (document.documentType) {
        params.documentType = document.documentType as PureDocumentType;
      }

      return investorProductDocumentsApi.adminInvestorProductDocumentsCreate(params).then(
        (result) => ({ status: 'fulfilled' as const, fileName: file.name, value: result }),
        (error) => ({ status: 'rejected' as const, fileName: file.name, reason: error }),
      );
    });

    const results = await Promise.all(uploadPromises);

    return { results, oversizedFiles };
  };

  const onSubmit = useCallback(
    async (fieldsValues: StateValues<AddDocumentsFields>) => {
      if (!selectedProduct || documentsUploaded.length === 0) return;

      const documents = documentsUploaded.filter((doc) => doc.document.file).map((doc) => doc.document);

      try {
        await withApi(async () => {
          setLoading(true);
          const { results, oversizedFiles } = await processBatchUploads(documents, selectedProduct.id);
          setLoading(false);

          const newFileErrors: Record<string, string[]> = {};
          const successfulUploadedDocuments: string[] = [];

          oversizedFiles.forEach((doc) => {
            newFileErrors[(doc.file as File).name] = ['sizeExceeded'];
          });

          results.forEach((r) => {
            if (r.status === 'rejected') {
              newFileErrors[r.fileName] = r.reason?.info?.error_codes;
            } else {
              successfulUploadedDocuments.push(r.value.id);
            }
          });

          const totalFiles = documents.length;
          const failedCount = totalFiles - successfulUploadedDocuments.length;
          setFileErrors(newFileErrors);
          setNoOfDocumentsToUpload(totalFiles);
          setSuccessfulUploadedDocumentIds(successfulUploadedDocuments);

          // update the modal state
          let stateToSet = ModalState.VALID;
          if (failedCount === totalFiles) {
            stateToSet = ModalState.INVALID;
          } else if (failedCount > 0) {
            stateToSet = ModalState.PARTIAL_VALID;
          }
          setModalState(stateToSet);
        });
      } catch (error) {
        console.error(error);
        setModalState(ModalState.INVALID);
      }
    },
    [selectedProduct, documentsUploaded, onAddNewDocument],
  );

  const onDiscardAllDocuments = useCallback(async () => {
    const deletionPromises = successfulUploadedDocumentIds.map((id) => {
      const request: AdminInvestorProductDocumentsDestroyRequest = { id };
      return investorProductDocumentsApi.adminInvestorProductDocumentsDestroy(request);
    });

    try {
      setLoading(true);
      await Promise.all(deletionPromises);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }

    onClose();
  }, [successfulUploadedDocumentIds, onClose, investorProductDocumentsApi, setSuccessfulUploadedDocumentIds]);

  const getDocumentTypes = useCallback(() => {
    return Object.values(PureDocumentType)
      .filter((documentType) => documentType !== PureDocumentType.NOT_SPECIFIED)
      .map((documentType) => {
        return {
          label: translate(`documentTypes.${snakeToCamelCaseWithNumbers(documentType)}`),
          value: documentType,
        };
      });
  }, []);

  const fetchProducts = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        const response = await adminApi.adminProductsList({
          permission: AdminProductsListPermissionEnum.MANAGE_INVESTOR_DOCUMENTS,
          search: searchQuery,
          limit: 100,
        });
        setProducts(response.results || []);
      });
    },
    [withApi, adminApi],
  );

  const searchProductValue = useCallback(
    (value: string) => {
      fetchProducts(value);
    },
    [fetchProducts],
  );

  const debouncedProductSearch = useMemo(() => debounce(searchProductValue, 500), [searchProductValue]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const onAddValidDocuments = useCallback(() => {
    setModalState(ModalState.VALID);
  }, []);

  return {
    modalState,
    successfulUploadedDocumentIds,
    noOfDocumentsToUpload,
    products,
    debouncedProductSearch,
    apiError,
    fileErrors,
    loading,
    getDocumentTypes,
    setSelectedProduct,
    onDiscardAllDocuments,
    onAddValidDocuments,
    onChangeBatchDocuments,
    onSubmit,
  };
};
